import type { Activity, ActivityWithProgram, Program } from '@parinaam-vms/shared';
import { apiDelete, apiGet, apiGetPage, apiPatch, apiPost, toQueryString } from './client';
import type { Page } from './client';

// ---------------------------------------------------------------------------
// Programs
// ---------------------------------------------------------------------------

/** A `type`, not an `interface`, so it can be passed to `toQueryString`. */
export type ProgramListQuery = {
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
};

/** The detail carries a count of its activities, so the screen needs one request. */
export interface ProgramDetail extends Program {
  activity_count: number;
}

export type ProgramBody = Partial<
  Pick<Program, 'program_code' | 'name' | 'description' | 'status' | 'start_date' | 'end_date'>
>;

export function fetchPrograms(params: ProgramListQuery): Promise<Page<Program>> {
  return apiGetPage<Program>(`/programs${toQueryString(params)}`);
}

export function fetchProgram(id: string): Promise<ProgramDetail> {
  return apiGet<ProgramDetail>(`/programs/${id}`);
}

export function createProgram(body: ProgramBody): Promise<Program> {
  return apiPost<Program>('/programs', body);
}

export function updateProgram(id: string, body: ProgramBody): Promise<Program> {
  return apiPatch<Program>(`/programs/${id}`, body);
}

// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------

export type ActivityListQuery = {
  /** Several: the attendance screen's programme filter is a multi-select. */
  program_id?: string[];
  status?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  page_size?: number;
};

export type ActivityBody = Partial<
  Pick<
    Activity,
    | 'activity_code'
    | 'name'
    | 'description'
    | 'start_date'
    | 'end_date'
    | 'default_duration_minutes'
    | 'start_time'
    | 'end_time'
    | 'location'
    | 'status'
  >
>;

/**
 * Creation additionally names the program; an update never can.
 *
 * `volunteers_needed` is not an `Activity` field — migration 043 put the cap on
 * the session, not the activity — but every activity is given one default
 * session the moment it exists, and this is what sets that session's cap
 * without staff first needing to know to visit "Add a session".
 */
export type NewActivityBody = ActivityBody & { program_id: string; volunteers_needed?: number | null };

export function fetchActivities(params: ActivityListQuery): Promise<Page<ActivityWithProgram>> {
  return apiGetPage<ActivityWithProgram>(`/activities${toQueryString(params)}`);
}

export function fetchActivity(id: string): Promise<ActivityWithProgram> {
  return apiGet<ActivityWithProgram>(`/activities/${id}`);
}

export function createActivity(body: NewActivityBody): Promise<ActivityWithProgram> {
  return apiPost<ActivityWithProgram>('/activities', body);
}

export function updateActivity(id: string, body: ActivityBody): Promise<ActivityWithProgram> {
  return apiPatch<ActivityWithProgram>(`/activities/${id}`, body);
}

// ---------------------------------------------------------------------------
// Deletion (M4) — admin only, one way
// ---------------------------------------------------------------------------

/**
 * A soft delete. The program keeps its row and moves to the terminal
 * `DELETED` status; its activities go with it, and the count of how many comes
 * back so the screen can say what the cascade reached.
 */
export interface DeletedProgram extends Program {
  activities_cancelled: number;
  /** Sessions beneath them, cancelled by the same act (9 Oct 2026). */
  sessions_cancelled: number;
}

export function deleteProgram(id: string, reason: string): Promise<DeletedProgram> {
  return apiDelete<DeletedProgram>(`/programs/${id}`, { reason });
}

export function deleteActivity(id: string, reason: string): Promise<ActivityWithProgram> {
  return apiDelete<ActivityWithProgram>(`/activities/${id}`, { reason });
}
